import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Phone } from 'lucide-react';
import { PRODUCTS, CATEGORIES, Category, Product } from '../data/products';
import { Modal } from './ui/Modal';
import { ImageSlider } from './ui/ImageSlider';
const categoryLabels: Record<Category, string> = {
  All: 'Всі',
  Pants: 'Штани',
  Blouses: 'Блузи',
  Shirts: 'Сорочки',
  Jackets: 'Жакети',
  Dresses: 'Сукні',
  Tops: 'Майки',
  Vests: 'Жилети',
  Corsets: 'Корсети',
  Skirts: 'Спідниці'
};
export function Catalog() {
  const [activeCategory, setActiveCategory] = useState<Category>('All');
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const filteredProducts = activeCategory === 'All' ? PRODUCTS : PRODUCTS.filter(p => p.category === activeCategory);
  const handleContactClick = (e: React.MouseEvent, product: Product) => {
    e.stopPropagation();
    // In a real app, this might open a specific contact form or link
    window.location.href = `tel:+380964797313`;
  };
  return <section id="catalog" className="py-24 bg-white" data-id="element-34">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-35">
        <div className="text-center mb-12" data-id="element-36">
          <h2 className="text-4xl md:text-5xl font-serif text-brand-dark mb-4" data-id="element-37">
            Наш каталог
          </h2>
          <p className="text-brand-dark/70 max-w-2xl mx-auto mb-10" data-id="element-38">
            Відкрийте для себе повний асортимент преміального одягу, створеного
            з увагою до кожної деталі.
          </p>

          {/* Category Filter */}
          <div className="flex flex-wrap justify-center gap-3" data-id="element-39">
            {CATEGORIES.map(category => <button key={category} onClick={() => setActiveCategory(category)} className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${activeCategory === category ? 'bg-brand-burgundy text-white' : 'bg-brand-cream text-brand-dark hover:bg-brand-beige'}`} data-id="element-40">
                {categoryLabels[category]}
              </button>)}
          </div>
        </div>

        {/* Product Grid */}
        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8" data-id="element-41">
          <AnimatePresence mode="popLayout" data-id="element-42">
            {filteredProducts.map(product => <motion.div key={product.id} layout initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.9
          }} transition={{
            duration: 0.3
          }} className="group cursor-pointer flex flex-col" onClick={() => setSelectedProduct(product)} data-id="element-43">
                <div className="relative aspect-[3/4] overflow-hidden rounded-xl mb-4 bg-brand-cream" data-id="element-44">
                  <img src={product.image} alt={product.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" data-id="element-45" />
                  <div className="absolute inset-0 bg-brand-dark/0 group-hover:bg-brand-dark/10 transition-colors duration-300" data-id="element-46" />
                </div>

                <div className="flex-grow flex flex-col" data-id="element-47">
                  <div className="flex justify-between items-start mb-2" data-id="element-48">
                    <h3 className="text-lg font-serif font-medium text-brand-dark pr-4" data-id="element-49">
                      {product.name}
                    </h3>
                    <span className="text-brand-burgundy font-medium whitespace-nowrap" data-id="element-50">
                      ₴{product.price}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-1 mb-4" data-id="element-51">
                    {product.sizes.map(size => <span key={size} className="text-xs px-2 py-1 bg-brand-cream text-brand-dark/70 rounded" data-id="element-52">
                        {size}
                      </span>)}
                  </div>

                  <button onClick={e => handleContactClick(e, product)} className="mt-auto w-full py-3 border border-brand-burgundy text-brand-burgundy rounded-full text-sm font-medium hover:bg-brand-burgundy hover:text-white transition-colors flex items-center justify-center gap-2" data-id="element-53">
                    <MessageCircle className="w-4 h-4" data-id="element-54" />
                    Дізнатися / Замовити
                  </button>
                </div>
              </motion.div>)}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Product Detail Modal */}
      <Modal isOpen={!!selectedProduct} onClose={() => setSelectedProduct(null)} data-id="element-55">
        {selectedProduct && <div className="flex flex-col md:flex-row h-full" data-id="element-56">
            <div className="w-full md:w-1/2 h-[50vh] md:h-auto min-h-[400px] overflow-hidden rounded-t-xl md:rounded-l-xl md:rounded-tr-none" data-id="element-57">
              {selectedProduct.images && selectedProduct.images.length > 1 ? <ImageSlider images={selectedProduct.images} alt={selectedProduct.name} data-id="element-58" /> : <img src={selectedProduct.image} alt={selectedProduct.name} className="w-full h-full object-cover" data-id="element-59" />}
            </div>
            <div className="w-full md:w-1/2 p-8 md:p-12 flex flex-col" data-id="element-60">
              <span className="text-sm uppercase tracking-wider text-brand-brown mb-2" data-id="element-61">
                {categoryLabels[selectedProduct.category]}
              </span>
              <h2 className="text-3xl md:text-4xl font-serif text-brand-dark mb-4" data-id="element-62">
                {selectedProduct.name}
              </h2>
              <p className="text-2xl text-brand-burgundy mb-6" data-id="element-63">
                ₴{selectedProduct.price}
              </p>

              <div className="prose prose-sm text-brand-dark/80 mb-8" data-id="element-64">
                <p data-id="element-65">{selectedProduct.description}</p>
              </div>

              <div className="mb-8" data-id="element-66">
                <h4 className="text-sm font-medium text-brand-dark mb-3" data-id="element-67">
                  Доступні розміри
                </h4>
                <div className="flex flex-wrap gap-2" data-id="element-68">
                  {selectedProduct.sizes.map(size => <span key={size} className="w-10 h-10 flex items-center justify-center border border-brand-beige rounded text-sm text-brand-dark" data-id="element-69">
                      {size}
                    </span>)}
                </div>
              </div>

              {selectedProduct.colors && selectedProduct.colors.length > 0 && <div className="mb-8" data-id="element-70">
                  <h4 className="text-sm font-medium text-brand-dark mb-3" data-id="element-71">
                    Доступні кольори
                  </h4>
                  <div className="flex flex-wrap gap-2" data-id="element-72">
                    {selectedProduct.colors.map(color => <span key={color} className="px-3 py-1.5 border border-brand-beige rounded-full text-sm text-brand-dark bg-brand-cream" data-id="element-73">
                        {color}
                      </span>)}
                  </div>
                </div>}

              <div className="mt-auto space-y-3" data-id="element-74">
                <p className="text-sm text-brand-dark/60 mb-4 text-center" data-id="element-75">
                  Зв'яжіться з нами, щоб перевірити наявність та зробити
                  замовлення.
                </p>
                <a href="https://t.me/username" target="_blank" rel="noopener noreferrer" className="w-full py-4 bg-[#0088cc] text-white rounded-full text-sm font-medium hover:bg-[#0077b3] transition-colors flex items-center justify-center gap-2" data-id="element-76">
                  <MessageCircle className="w-5 h-5" data-id="element-77" />
                  Замовити через Telegram
                </a>
                <a href="tel:+380964797313" className="w-full py-4 bg-brand-burgundy text-white rounded-full text-sm font-medium hover:bg-brand-burgundy/90 transition-colors flex items-center justify-center gap-2" data-id="element-78">
                  <Phone className="w-5 h-5" data-id="element-79" />
                  Зателефонувати для замовлення
                </a>
              </div>
            </div>
          </div>}
      </Modal>
    </section>;
}